// Slide-over create/edit forms for Stage and Group, matching the design.
// These are the simple controlled forms; they reuse the exact mutation payloads
// from the original modals (createStage/updateStage, createGroup/updateGroup).

import React, { useState, useEffect } from 'react'
import { CircularProgress } from '@material-ui/core'
import { useMutation, useQueryClient } from 'react-query'
import { SlideOver, Field, TextInput, SelectInput, Button } from '../Primitives'
import TOKENS from '../tokens'
import QueryKeys from '../../../../utils/constants/queryKeys'
import createStage from '../../../../Apis/stages/createStage'
import updateStage from '../../../../Apis/stages/updateStage'
import createGroup from '../../../../Apis/groups/createGroup'
import updateGroup from '../../../../Apis/groups/updateGroup'
import { GROUP_TYPES_TO_LECTICAL } from '../../../../utils/constants/groupTypes'

const pad = (n) => String(n).padStart(2, '0')
const toInputDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
const toIso = (value) => (value ? new Date(value).toISOString() : null)

/* ------------------------------------------------------------- Stage form -- */

export const StageSlideOver = ({ open, onClose, orgId, seasonId, stage }) => {
  const isEdit = Boolean(stage)
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')

  useEffect(() => {
    if (open) {
      setName(stage?.name || '')
      setStart(toInputDate(stage?.startDate))
      setEnd(toInputDate(stage?.endDate))
    }
  }, [open, stage])

  const invalidate = () => {
    queryClient.invalidateQueries([QueryKeys.GET_STAGES, orgId, seasonId])
    onClose()
  }
  const createMutation = useMutation((data) => createStage({ orgId, seasonId, data }), { onSuccess: invalidate })
  const updateMutation = useMutation(({ stageId, data }) => updateStage({ orgId, seasonId, stageId, data }), { onSuccess: invalidate })
  const isLoading = createMutation.isLoading || updateMutation.isLoading

  const submit = () => {
    const payload = { seasonId, name, startDate: toIso(start), endDate: toIso(end) }
    if (isEdit) updateMutation.mutate({ stageId: stage.id, data: payload })
    else createMutation.mutate(payload)
  }

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Stage' : 'New Stage'}
      subtitle={isEdit ? 'Update stage name and dates.' : 'Stages divide a season into phases (Regular Season, Playoffs, Finals).'}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={isLoading || !name.trim()}>
            {isLoading ? <CircularProgress size={18} style={{ color: TOKENS.white }} /> : isEdit ? 'Update' : 'Create Stage'}
          </Button>
        </>
      )}
    >
      <Field label="Stage name">
        <TextInput placeholder="e.g. Regular Season, Playoffs, Finals" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Start date"><TextInput type="date" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
        <Field label="End date"><TextInput type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
      </div>
    </SlideOver>
  )
}

/* ------------------------------------------------------------- Group form -- */

export const GroupSlideOver = ({ open, onClose, orgId, seasonId, stageId, group }) => {
  const isEdit = Boolean(group)
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [type, setType] = useState('RoundRobin')

  useEffect(() => {
    if (open) {
      setName(group?.name || '')
      setType(group?.type || 'RoundRobin')
    }
  }, [open, group])

  const invalidate = () => {
    queryClient.invalidateQueries([QueryKeys.GET_STAGES, orgId, seasonId])
    onClose()
  }
  const createMutation = useMutation((data) => createGroup({ orgId, seasonId, data }), { onSuccess: invalidate })
  const updateMutation = useMutation(({ groupId, data }) => updateGroup({ orgId, seasonId, groupId, data }), { onSuccess: invalidate })
  const isLoading = createMutation.isLoading || updateMutation.isLoading

  const submit = () => {
    const payload = { name, type }
    if (isEdit) updateMutation.mutate({ groupId: group.id, data: payload })
    else createMutation.mutate({ ...payload, stageId })
  }

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Group' : 'New Group'}
      subtitle={isEdit ? 'Update group name and format.' : 'Groups split a stage into pools or knockout brackets.'}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={isLoading || !name.trim()}>
            {isLoading ? <CircularProgress size={18} style={{ color: TOKENS.white }} /> : isEdit ? 'Update' : 'Create Group'}
          </Button>
        </>
      )}
    >
      <Field label="Group name">
        <TextInput placeholder="e.g. Group A, Group 1" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <Field label="Type">
        <SelectInput
          value={type}
          onChange={(e) => setType(e.target.value)}
          options={GROUP_TYPES_TO_LECTICAL.map((o) => ({ value: o.value, label: o.label }))}
        />
      </Field>
    </SlideOver>
  )
}
